import {
  BookOpen,
  Building2,
  Check,
  CheckSquare,
  ChevronDown,
  ClipboardList,
  FileText,
  Globe,
  HandCoins,
  HelpCircle,
  LayoutDashboard,
  ListChecks,
  Mail,
  Megaphone,
  Plus,
  Route as RouteIcon,
  Send,
  Settings,
  User,
  Workflow,
  Wrench,
} from "lucide-react";
import { useStore } from "ra-core";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CanAccess, useTranslate, useUserMenu } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";
import { RefreshButton } from "@/components/admin/refresh-button";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { UserMenu } from "@/components/admin/user-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { ChangelogPage } from "../misc/ChangelogPage";
import { ROUTE_ARTICLE } from "../help/helpArticles";
import { REPORT_META } from "../reports/reportMeta";
import { BccCopyButton } from "./BccCopyButton";
import { NotificationBell } from "./NotificationBell";
import { SlateSearch } from "./SlateSearch";

const Header = () => {
  const { darkModeLogo, lightModeLogo, title, moduleFlags } =
    useConfigurationContext();
  const reportsVisible = moduleFlags?.reports !== false;
  const inboxVisible = moduleFlags?.inbox !== false;
  const formsVisible = moduleFlags?.forms !== false;
  const sequencesVisible = moduleFlags?.sequences !== false;
  const workflowsVisible = moduleFlags?.workflows !== false;
  const campaignsVisible = moduleFlags?.campaigns !== false;
  const location = useLocation();
  const translate = useTranslate();

  let currentPath: string | boolean = "/";
  if (matchPath("/", location.pathname)) {
    currentPath = "/";
  } else if (matchPath("/contacts/*", location.pathname)) {
    currentPath = "/contacts";
  } else if (matchPath("/companies/*", location.pathname)) {
    currentPath = "/companies";
  } else if (matchPath("/deals/*", location.pathname)) {
    currentPath = "/deals";
  } else if (matchPath("/tasks/*", location.pathname)) {
    currentPath = "/tasks";
  } else if (matchPath("/inbox/*", location.pathname)) {
    currentPath = "/inbox";
  } else if (matchPath("/reports/*", location.pathname)) {
    currentPath = "/reports";
  } else if (matchPath("/marketing/*", location.pathname)) {
    currentPath = "/marketing";
  } else {
    currentPath = false;
  }

  return (
    <>
      <nav className="grow">
        <header className="bg-secondary">
          <div className="px-4">
            <div className="flex justify-between items-center flex-1 gap-6">
              <Link
                to="/"
                className="flex items-center gap-2 text-secondary-foreground no-underline shrink-0"
              >
                <img
                  className="[.light_&]:hidden h-6"
                  src={darkModeLogo}
                  alt={title}
                />
                <img
                  className="[.dark_&]:hidden h-6"
                  src={lightModeLogo}
                  alt={title}
                />
                <h1 className="text-xl font-semibold">{title}</h1>
              </Link>
              <div>
                <nav className="flex">
                  <NavigationTab
                    label={translate("ra.page.dashboard")}
                    to="/"
                    isActive={currentPath === "/"}
                  />
                  <NavigationTab
                    label={translate("resources.contacts.name", {
                      smart_count: 2,
                    })}
                    to="/contacts"
                    isActive={currentPath === "/contacts"}
                  />
                  <NavigationTab
                    label={translate("resources.companies.name", {
                      smart_count: 2,
                    })}
                    to="/companies"
                    isActive={currentPath === "/companies"}
                  />
                  <NavigationTab
                    label={translate("resources.deals.name", {
                      smart_count: 2,
                    })}
                    to="/deals"
                    isActive={currentPath === "/deals"}
                  />
                  <NavigationTab
                    label="Tasks"
                    to="/tasks"
                    isActive={currentPath === "/tasks"}
                  />
                  {inboxVisible && (
                    <NavigationTab
                      label="Inbox"
                      to="/inbox"
                      isActive={currentPath === "/inbox"}
                    />
                  )}
                  {reportsVisible && (
                    <ReportsNavMenu isActive={currentPath === "/reports"} />
                  )}
                  <MarketingNavMenu
                    isActive={currentPath === "/marketing"}
                    formsVisible={formsVisible}
                    sequencesVisible={sequencesVisible}
                    workflowsVisible={workflowsVisible}
                    campaignsVisible={campaignsVisible}
                  />
                </nav>
              </div>
              <div className="flex items-center gap-2">
                <BccCopyButton />
                <SlateSearch />
                <QuickCreateMenu />
                <NotificationBell />
                <HelpMenu currentPath={currentPath} />
                <CanAccess resource="configuration" action="edit">
                  <Link
                    to="/settings"
                    aria-label="Settings"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md text-secondary-foreground/70 hover:text-secondary-foreground hover:bg-secondary-foreground/10 transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                  </Link>
                </CanAccess>
                <ThemeModeToggle />
                <RefreshButton />
                <UserMenu>
                  <ProfileMenu />
                  <ChangelogMenuItem />
                </UserMenu>
              </div>
            </div>
          </div>
        </header>
      </nav>
    </>
  );
};

/** Page-aware "what is this screen" guidance, shortcuts, and support. */
const PAGE_GUIDE: Record<string, { title: string; body: string }> = {
  "/": {
    title: "Dashboard",
    body: "Your home view. Switch between the Overview and any dashboard you've built with the selector in the upper right - the choice sticks. 'Keep an eye on' surfaces heating-up leads, slipping deals, and overdue follow-ups.",
  },
  "/contacts": {
    title: "Contacts",
    body: "Everyone you talk to. Filter from the left rail, import from any CSV with automatic field mapping, and open a contact for the full timeline, lead score, and attribution journey.",
  },
  "/companies": {
    title: "Companies",
    body: "Accounts, including holding-company hierarchies with group roll-ups. Customize which info modules show on a company's sidebar with the Customize button.",
  },
  "/deals": {
    title: "Deals",
    body: "The pipeline. Drag deals between stages - moving one to Won or Lost asks for the final numbers so reporting stays honest. Click a deal for its full record page.",
  },
  "/tasks": {
    title: "Tasks",
    body: "Your month at a glance with tasks on their due dates, plus the grouped list. Sync Google or Microsoft calendar at deployment to see meetings here too.",
  },
  "/inbox": {
    title: "Inbox",
    body: "Your mailbox inside Obvious. Live two-way sync (Google/Microsoft) lands at deployment - every email auto-logs to the matching contact's timeline. Emails you send from contact records show here too.",
  },
  "/reports": {
    title: "Reports",
    body: "Build reports with the stepped builder, prove revenue with Attribution, check Web & Ads data, hand clients the monthly Scorecard, and install ready-made dashboards from the library.",
  },
  "/marketing": {
    title: "Marketing",
    body: "Forms, branded emails, lists, sequences, workflows (with lead scoring), and campaigns with open/click analytics. Sends and syncs go live with your deployment.",
  },
};

const HelpMenu = ({ currentPath }: { currentPath: string | boolean }) => {
  const [gs, setGs] = useStore<{ dismissed?: boolean }>(
    "slate.gettingStarted",
    {},
  );
  const location = useLocation();
  const guide =
    typeof currentPath === "string" ? PAGE_GUIDE[currentPath] : undefined;
  // Exact path first (settings pages, marketing/report tabs), then the
  // normalized section, so "Read the full guide" works everywhere.
  const articleSlug =
    ROUTE_ARTICLE[location.pathname] ??
    (typeof currentPath === "string" ? ROUTE_ARTICLE[currentPath] : undefined);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Help"
          className="inline-flex items-center justify-center w-8 h-8 rounded-md text-secondary-foreground/70 hover:text-secondary-foreground hover:bg-secondary-foreground/10 transition-colors"
        >
          <HelpCircle className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 flex flex-col gap-3" align="end">
        <Link
          to="/help"
          className="flex items-center gap-2 text-sm font-medium hover:underline"
        >
          <BookOpen className="w-4 h-4 text-muted-foreground" /> Open the help
          center
        </Link>
        {(guide || articleSlug) && (
          <div className="border-t border-border pt-2">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium mb-1">
              About this page
            </p>
            {guide && (
              <>
                <p className="text-sm font-medium">{guide.title}</p>
                <p className="text-xs text-muted-foreground leading-5">
                  {guide.body}
                </p>
              </>
            )}
            {articleSlug && (
              <Link
                to={`/help/${articleSlug}`}
                className="text-xs underline hover:no-underline inline-block mt-1"
              >
                Read the full guide
              </Link>
            )}
          </div>
        )}
        <div>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium mb-1">
            Shortcuts
          </p>
          <p className="text-xs text-muted-foreground">
            <kbd className="border border-border rounded px-1">⌘K</kbd> or{" "}
            <kbd className="border border-border rounded px-1">/</kbd> - find
            any contact, company, or deal
          </p>
        </div>
        <div className="flex flex-col gap-1 border-t border-border pt-2">
          {gs.dismissed && (
            <button
              type="button"
              className="text-left text-sm hover:underline"
              onClick={() => setGs({ ...gs, dismissed: false })}
            >
              Reopen the Getting started checklist
            </button>
          )}
          <Link to={ChangelogPage.path} className="text-sm hover:underline">
            What's new
          </Link>
          <p className="text-xs text-muted-foreground">
            Need a hand? Your account manager is one email away - support
            contact is set per workspace at deployment.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const NavigationTab = ({
  label,
  to,
  isActive,
}: {
  label: string;
  to: string;
  isActive: boolean;
}) => (
  <Link
    to={to}
    className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
      isActive
        ? "text-secondary-foreground border-secondary-foreground"
        : "text-secondary-foreground/70 border-transparent hover:text-secondary-foreground/80"
    }`}
  >
    {label}
  </Link>
);


const ProfileMenu = () => {
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ProfileMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/settings/profile" className="flex items-center gap-2">
        <User />
        Profile &amp; Preferences
      </Link>
    </DropdownMenuItem>
  );
};




const ChangelogMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ChangelogMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ChangelogPage.path} className="flex items-center gap-2">
        <FileText />
        {translate("crm.changelog.title")}
      </Link>
    </DropdownMenuItem>
  );
};

/** Shared styling for a top-nav dropdown trigger that matches NavigationTab. */
const navTriggerClass = (isActive: boolean) =>
  `inline-flex items-center gap-1 px-6 py-3 text-sm font-medium transition-colors border-b-2 outline-none ${
    isActive
      ? "text-secondary-foreground border-secondary-foreground"
      : "text-secondary-foreground/70 border-transparent hover:text-secondary-foreground/80"
  }`;

const NavMenuItem = ({
  to,
  icon: Icon,
  label,
  description,
  active,
}: {
  to: string;
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  description?: string;
  active?: boolean;
}) => (
  <DropdownMenuItem asChild className={active ? "bg-primary/10" : undefined}>
    <Link to={to} className="flex items-start gap-2 w-full">
      {Icon && <Icon className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />}
      <span className="flex-1 min-w-0">
        <span className="block text-sm">{label}</span>
        {description && (
          <span className="block text-[11px] text-muted-foreground leading-4">
            {description}
          </span>
        )}
      </span>
      {active && <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
    </Link>
  </DropdownMenuItem>
);

const MARKETING_GROUPS: {
  label: string;
  items: { to: string; label: string; icon: React.ComponentType<{ className?: string }> }[];
}[] = [
  {
    label: "Capture & reach",
    items: [
      { to: "/marketing/forms", label: "Forms", icon: ListChecks },
      { to: "/marketing/emails", label: "Emails", icon: Mail },
      { to: "/marketing/campaigns", label: "Campaigns", icon: Megaphone },
    ],
  },
  {
    label: "Audience & automation",
    items: [
      { to: "/marketing/lists", label: "Lists", icon: ListChecks },
      { to: "/marketing/sequences", label: "Sequences", icon: Send },
      { to: "/marketing/workflows", label: "Workflows", icon: Workflow },
    ],
  },
];

const MarketingNavMenu = ({
  isActive,
  formsVisible,
  sequencesVisible,
  workflowsVisible,
  campaignsVisible,
}: {
  isActive: boolean;
  formsVisible: boolean;
  sequencesVisible: boolean;
  workflowsVisible: boolean;
  campaignsVisible: boolean;
}) => {
  const location = useLocation();
  const hiddenPaths = [
    !formsVisible && "/marketing/forms",
    !sequencesVisible && "/marketing/sequences",
    !workflowsVisible && "/marketing/workflows",
    !campaignsVisible && "/marketing/campaigns",
  ].filter(Boolean);
  const groups = MARKETING_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((item) => !hiddenPaths.includes(item.to)),
  })).filter((g) => g.items.length > 0);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={navTriggerClass(isActive)}>
        Marketing
        <ChevronDown className="w-3.5 h-3.5 transition-transform data-[state=open]:rotate-180" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-60">
        {groups.map((g, gi) => (
          <div key={g.label}>
            {gi > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
              {g.label}
            </DropdownMenuLabel>
            {g.items.map((item) => (
              <NavMenuItem
                key={item.to}
                {...item}
                active={location.pathname.startsWith(item.to)}
              />
            ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const REPORT_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  library: LayoutDashboard,
  scorecard: ClipboardList,
  attribution: RouteIcon,
  web: Globe,
  builder: Wrench,
};

const ReportsNavMenu = ({ isActive }: { isActive: boolean }) => {
  const location = useLocation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={navTriggerClass(isActive)}>
        Reports
        <ChevronDown className="w-3.5 h-3.5 transition-transform data-[state=open]:rotate-180" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        {REPORT_META.map((r) => {
          const to = r.slug === "library" ? "/reports" : `/reports/${r.slug}`;
          const active =
            r.slug === "library"
              ? location.pathname === "/reports" || location.pathname === "/reports/library"
              : location.pathname.startsWith(`/reports/${r.slug}`);
          return (
            <NavMenuItem
              key={r.slug}
              to={to}
              label={r.label}
              icon={REPORT_ICONS[r.slug]}
              active={active}
            />
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** Quick create - the "+" next to search, HubSpot style. */
const QuickCreateMenu = () => (
  <DropdownMenu>
    <DropdownMenuTrigger
      aria-label="Create"
      className="inline-flex items-center justify-center w-8 h-8 rounded-md text-secondary-foreground/70 hover:text-secondary-foreground hover:bg-secondary-foreground/10 transition-colors"
    >
      <Plus className="w-4 h-4" />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-48">
      <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
        Create
      </DropdownMenuLabel>
      <NavMenuItem to="/contacts/create" label="Contact" icon={User} />
      <NavMenuItem to="/companies/create" label="Company" icon={Building2} />
      <NavMenuItem to="/deals/create" label="Deal" icon={HandCoins} />
      <NavMenuItem to="/tasks" label="Task" icon={CheckSquare} />
      <DropdownMenuSeparator />
      <NavMenuItem to="/settings/import-export" label="Import contacts" icon={FileText} />
    </DropdownMenuContent>
  </DropdownMenu>
);

export default Header;
